// src/hooks/useI18n.js - Hook personnalisé pour faciliter l'usage d'i18n
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

export const useI18n = () => {
  const { t, i18n } = useTranslation();

  // Configuration RTL/LTR automatique
  useEffect(() => {
    const currentLang = i18n.language;
    const isRTL = currentLang === 'ar';
    
    // Appliquer la direction au document
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;
    
    // Ajouter/retirer la classe RTL pour le styling CSS
    if (isRTL) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);

  const getCurrentLanguage = () => {
    return i18n.language || 'fr';
  };

  const isRTL = () => {
    return i18n.language === 'ar';
  };

  const getLanguageInfo = () => {
    const languages = {
      fr: { name: 'Français', flag: '🇫🇷', dir: 'ltr' },
      en: { name: 'English', flag: '🇺🇸', dir: 'ltr' },
      es: { name: 'Español', flag: '🇪🇸', dir: 'ltr' },
      ar: { name: 'العربية', flag: '🇲🇦', dir: 'rtl' }
    };
    return languages[getCurrentLanguage()] || languages.fr;
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  // Fonction helper pour les traductions avec valeurs par défaut
  const translate = (key, defaultValue = '', options = {}) => {
    return t(key, defaultValue, options);
  };

  // Fonction pour traduire des arrays
  const translateArray = (keyPrefix, defaultArray = []) => {
    const result = [];
    let index = 0;
    
    while (true) {
      const key = `${keyPrefix}.${index}`;
      const translation = t(key, null);
      
      if (translation === null || translation === key) {
        break;
      }
      
      result.push(translation);
      index++;
    }
    
    return result.length > 0 ? result : defaultArray;
  };

  return {
    t: translate,
    i18n,
    currentLanguage: getCurrentLanguage(),
    isRTL: isRTL(),
    languageInfo: getLanguageInfo(),
    changeLanguage,
    translateArray
  };
};

// Hook spécialisé pour les formulaires avec gestion d'erreurs
export const useI18nForm = () => {
  const { t, isRTL } = useI18n();

  const getFieldDirection = () => {
    return isRTL ? 'rtl' : 'ltr';
  };

  const getTextAlign = () => {
    return isRTL ? 'text-right' : 'text-left';
  };

  const getFlexDirection = () => {
    return isRTL ? 'flex-row-reverse' : 'flex-row';
  };

  const translateError = (errorKey, fallbackMessage = '') => {
    return t(`auth.errors.${errorKey}`, fallbackMessage);
  };

  return {
    t,
    isRTL,
    getFieldDirection,
    getTextAlign,
    getFlexDirection,
    translateError
  };
};

export default useI18n;